import React, { FC, useEffect, useRef, useState } from "react"
import axios from 'axios'

import { debounce, isClientSide } from "@utils"
import { CardFragmentFragment } from "@graphqlTypes"

import Cards, { CARDS_VARIANTS } from "@components/Cards/Cards"
import Icon, { ICON_TYPES } from "../Icon/Icon"

import SearchInput, { SEARCH_INPUT_SIZES } from "../SearchInput/SearchInput"

import "./Search.scss"

declare global {
  interface Window {
    ga: any
  }
}

type SearchProps = {
  onClickClose?: () => void,
}

const Search: FC<SearchProps> = ({ onClickClose }) => {
  const [results, setResults] = useState<CardFragmentFragment[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [query, setQuery] = useState<string>("")
  const debouncedHandleSearchRef = useRef<(_query?: string) => void | undefined>()

  const handleClickCard = (): void => {
    onClickClose && onClickClose()
  }

  const handleSearch = (_query?: string) => {
    axios
      .get(`/api/search?query=${_query}`)
      .then(response => response.data)
      .then(data => {
        setResults(data.results)
        setIsLoading(false)
        setHasError(false)
      })
      .catch(() => {
        setResults([])
        setIsLoading(false)
        setHasError(true)
      })
  }

  useEffect(() => {
    if (!isClientSide()) return
    debouncedHandleSearchRef.current = debounce(handleSearch, 300)
  }, [])

  useEffect(() => {
    if (!isClientSide() || isLoading) return

    const SearchInputEl = document.querySelector<HTMLInputElement>(".SearchInput input")

    if (SearchInputEl) {
      SearchInputEl.focus()
    }
  }, [isLoading])

  useEffect(() => {
    if (!isClientSide() || !debouncedHandleSearchRef.current) return

    debouncedHandleSearchRef.current(query)
  }, [query])

  return (
    <div className="Search">
      <button className="Search--CloseButton" onClick={onClickClose}>
        <Icon
          size="20px"
          type={ICON_TYPES["close"]}
        />
      </button>
      <div className="container">
        <div className="Search--Content">
          {!isLoading && results && !hasError && (
            <>
              <SearchInput
                onClearQuery={() => setQuery("")}
                onSearch={q => setQuery(q)}
                query={query}
                size={SEARCH_INPUT_SIZES.XL}
              />
              <div className="Search--Results">
                {results.length > 0 && (
                  <Cards
                    items={results}
                    onClickCard={handleClickCard}
                    variant={CARDS_VARIANTS.COMPACT_WITH_DESCRIPTION}
                  />
                )}
                {results.length === 0 && (
                  <p className="Search--NoResults">Sorry, no results found.</p>
                )}
              </div>
            </>
          )}
          {results.length === 0 && hasError && (
            <h4 style={{ textAlign: "center" }}>Oops, something went wrong.</h4>
          )}
        </div>
      </div>
      {isLoading && results.length === 0 && !hasError && (
        <div className="Search--Loading">Loading...</div>
      )}
    </div>
  )
}

export default Search
